import React from "react";
import {
  HIGH_BAND_MAX_INDEX,
  MODERATE_BAND_MAX_INDEX,
  STANDARD_SCORE_AXIS_MAX,
  STANDARD_SCORE_AXIS_MIN,
} from "@/lib/score-bands";
import { useMediaQuery } from "@/lib/useMediaQuery";
import type { OverallScoreBoxStats } from "@/lib/report-data";

interface OverallScoreBoxPlotProps {
  stats: OverallScoreBoxStats;
  showOutliers?: boolean;
  bandCutoffs?: { veryLowMax: number; lowMax: number };
}

interface BoxTip {
  /** Tooltip body text. */
  label: string;
  /** Horizontal position as a percentage of the chart's axis width
      (0–100), so the tooltip tracks the score it describes regardless
      of viewport size. */
  pct: number;
}

const AXIS_MIN = STANDARD_SCORE_AXIS_MIN;
const AXIS_MAX = STANDARD_SCORE_AXIS_MAX;
const AXIS_RANGE = AXIS_MAX - AXIS_MIN;

function scoreToX(value: number, width: number): number {
  return ((Math.max(AXIS_MIN, Math.min(AXIS_MAX, value)) - AXIS_MIN) / AXIS_RANGE) * width;
}

function scoreToPct(value: number): number {
  return ((Math.max(AXIS_MIN, Math.min(AXIS_MAX, value)) - AXIS_MIN) / AXIS_RANGE) * 100;
}

/* ─── Overall score distribution box-plot ───────────────────
   Box-plot rendered against the same coloured band tints used
   on the Position chart (Student page) so the two distribution
   visuals read as a single visual family. Bands sit behind a
   classic IQR box + median + P10/P90 whiskers. Each box-plot
   element is hover-interactive: cursor over a whisker / IQR
   edge / median surfaces the underlying score in a small dark
   tooltip above the chart, mirroring the BoxPlotRow tooltip
   pattern already used in Charts.tsx. */
export function OverallScoreBoxPlot({
  stats,
  showOutliers = false,
  bandCutoffs = { veryLowMax: HIGH_BAND_MAX_INDEX, lowMax: MODERATE_BAND_MAX_INDEX },
}: OverallScoreBoxPlotProps) {
  const isNarrow = useMediaQuery("(max-width: 640px)");
  const [tip, setTip] = React.useState<BoxTip | null>(null);
  const veryLowMax = bandCutoffs.veryLowMax;
  const lowMax = bandCutoffs.lowMax;
  const ticks = [
    { value: veryLowMax, label: Math.round(veryLowMax).toString() },
    { value: lowMax, label: Math.round(lowMax).toString() },
  ];

  const VB_W = 600;
  const PAD_TOP = 14;
  const PLOT_Y = 46;       // vertical centre line of the box-plot
  const BOX_H = 22;
  const AXIS_Y = 86;
  const VB_H = AXIS_Y + 6;

  const hasData = stats.n > 0;

  type BandKey = "very-low" | "low" | "on-track";
  interface BandSpec {
    key: BandKey;
    label: string;
    /** Compact label for narrow widths where the inner bands collapse
        (Very low + Low together cover only 25/80 of the axis). */
    shortLabel: string;
    from: number;
    to: number;
    bg: string;
    fg: string;
    stroke: string;
  }
  const BANDS: ReadonlyArray<BandSpec> = [
    {
      key: "very-low",
      label: "Very Low",
      shortLabel: "V. Low",
      from: AXIS_MIN,
      to: veryLowMax,
      bg: "var(--risk-concern-bg)",
      fg: "var(--risk-concern-fg)",
      stroke: "var(--risk-concern)",
    },
    {
      key: "low",
      label: "Low",
      shortLabel: "Low",
      from: veryLowMax,
      to: lowMax,
      bg: "var(--risk-emerging-bg)",
      fg: "var(--risk-emerging-fg)",
      stroke: "var(--risk-emerging)",
    },
    {
      key: "on-track",
      label: "On Track",
      shortLabel: "On Track",
      from: lowMax,
      to: AXIS_MAX,
      bg: "var(--risk-on-track-bg)",
      fg: "var(--risk-on-track-fg)",
      stroke: "var(--risk-on-track)",
    },
  ] as const;

  const showTip = (label: string, pct: number) => () => setTip({ label, pct });
  const hideTip = () => setTip(null);
  const outliers = React.useMemo(
    () =>
      showOutliers
        ? stats.outlierValues.map((value, index) => ({
            value,
            y: PLOT_Y + (index % 2 === 0 ? -10 : 10),
          }))
        : [],
    [showOutliers, stats.outlierValues],
  );

  return (
    <div>
      {/* SVG + tooltip layer share a position-relative wrapper so the
          tooltip can sit above the chart at the score's percentage. */}
      <div style={{ position: "relative" }}>
        {tip && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: "absolute",
              left: `${tip.pct}%`,
              bottom: "calc(100% + 6px)",
              transform: "translateX(-50%)",
              background: "var(--ink-800)",
              color: "#fff",
              fontSize: 11,
              fontWeight: 700,
              padding: "4px 9px",
              borderRadius: 6,
              whiteSpace: "nowrap",
              pointerEvents: "none",
              zIndex: 10,
              boxShadow: "var(--sh-2)",
            }}
          >
            {tip.label}
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "50%",
                bottom: -4,
                transform: "translateX(-50%)",
                width: 8,
                height: 8,
                background: "var(--ink-800)",
                clipPath: "polygon(0 0,100% 0,50% 100%)",
              }}
            />
          </div>
        )}

        <svg
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={
            hasData
              ? `Overall ENSSA standard score distribution for ${stats.n} students. Median ${stats.p50}, middle 50% from ${stats.p25} to ${stats.p75}.`
              : `Overall ENSSA standard score distribution — no data for this group.`
          }
          style={{ display: "block", width: "100%", height: VB_H + 4, overflow: "visible" }}
        >
          {/* Layer 1 — Band tints. Subtle so the box-plot owns the
              foreground; matches the tint approach on the Position
              chart's desktop view. */}
          {BANDS.map((band) => {
            const x = scoreToX(band.from, VB_W);
            const w = scoreToX(band.to, VB_W) - x;
            return (
              <rect
                key={`band-${band.key}`}
                x={x}
                y={PAD_TOP}
                width={w}
                height={AXIS_Y - PAD_TOP}
                fill={band.bg}
                opacity={0.6}
              />
            );
          })}

          {/* Quiet 1px dividers between bands. */}
          {BANDS.slice(0, -1).map((band) => (
            <line
              key={`cut-${band.key}`}
              x1={scoreToX(band.to, VB_W)}
              x2={scoreToX(band.to, VB_W)}
              y1={PAD_TOP}
              y2={AXIS_Y}
              stroke={band.stroke}
              strokeWidth={1}
              opacity={0.32}
            />
          ))}

          {/* Box-plot body */}
          {hasData && (
            <g>
              {/* P10–P90 connector line. Decorative — no tooltip. */}
              <line
                x1={scoreToX(stats.p10, VB_W)}
                x2={scoreToX(stats.p90, VB_W)}
                y1={PLOT_Y}
                y2={PLOT_Y}
                stroke="var(--ink-500)"
                strokeWidth={1.25}
              />

              {/* P10 whisker — visible 1.25px line + invisible wide hit
                  area for easy hover/touch activation. */}
              <line
                x1={scoreToX(stats.p10, VB_W)}
                x2={scoreToX(stats.p10, VB_W)}
                y1={PLOT_Y - 8}
                y2={PLOT_Y + 8}
                stroke="var(--ink-500)"
                strokeWidth={1.25}
              />
              <line
                x1={scoreToX(stats.p10, VB_W)}
                x2={scoreToX(stats.p10, VB_W)}
                y1={PLOT_Y - 14}
                y2={PLOT_Y + 14}
                stroke="transparent"
                strokeWidth={12}
                style={{ cursor: "pointer" }}
                onMouseEnter={showTip(`10th percentile · ENSSA standard score ${stats.p10}`, scoreToPct(stats.p10))}
                onMouseLeave={hideTip}
              />

              {/* P90 whisker. */}
              <line
                x1={scoreToX(stats.p90, VB_W)}
                x2={scoreToX(stats.p90, VB_W)}
                y1={PLOT_Y - 8}
                y2={PLOT_Y + 8}
                stroke="var(--ink-500)"
                strokeWidth={1.25}
              />
              <line
                x1={scoreToX(stats.p90, VB_W)}
                x2={scoreToX(stats.p90, VB_W)}
                y1={PLOT_Y - 14}
                y2={PLOT_Y + 14}
                stroke="transparent"
                strokeWidth={12}
                style={{ cursor: "pointer" }}
                onMouseEnter={showTip(`90th percentile · ENSSA standard score ${stats.p90}`, scoreToPct(stats.p90))}
                onMouseLeave={hideTip}
              />

              {/* IQR box (P25–P75). The body itself is hoverable and
                  shows the full IQR range. */}
              <rect
                x={scoreToX(stats.p25, VB_W)}
                y={PLOT_Y - BOX_H / 2}
                width={Math.max(1, scoreToX(stats.p75, VB_W) - scoreToX(stats.p25, VB_W))}
                height={BOX_H}
                rx={10}
                fill="color-mix(in oklab, var(--enssa-mauve) 22%, white)"
                stroke="var(--enssa-aubergine)"
                strokeWidth={1.25}
                style={{ cursor: "pointer" }}
                onMouseEnter={showTip(
                  `IQR · ${stats.p25}–${stats.p75}`,
                  scoreToPct((stats.p25 + stats.p75) / 2),
                )}
                onMouseLeave={hideTip}
              />

              {/* P25 left edge — wide invisible hit area sitting on
                  top of the IQR box edge so the percentile-specific
                  tooltip wins over the box body tooltip when the
                  cursor is right on the edge. */}
              <line
                x1={scoreToX(stats.p25, VB_W)}
                x2={scoreToX(stats.p25, VB_W)}
                y1={PLOT_Y - BOX_H / 2 - 2}
                y2={PLOT_Y + BOX_H / 2 + 2}
                stroke="transparent"
                strokeWidth={10}
                style={{ cursor: "pointer" }}
                onMouseEnter={showTip(`25th percentile · ENSSA standard score ${stats.p25}`, scoreToPct(stats.p25))}
                onMouseLeave={hideTip}
              />

              {/* P75 right edge. */}
              <line
                x1={scoreToX(stats.p75, VB_W)}
                x2={scoreToX(stats.p75, VB_W)}
                y1={PLOT_Y - BOX_H / 2 - 2}
                y2={PLOT_Y + BOX_H / 2 + 2}
                stroke="transparent"
                strokeWidth={10}
                style={{ cursor: "pointer" }}
                onMouseEnter={showTip(`75th percentile · ENSSA standard score ${stats.p75}`, scoreToPct(stats.p75))}
                onMouseLeave={hideTip}
              />

              {/* Median — invisible wide hit area below the visible
                  3px aubergine line so the median is comfortably
                  hoverable. */}
              <line
                x1={scoreToX(stats.p50, VB_W)}
                x2={scoreToX(stats.p50, VB_W)}
                y1={PLOT_Y - BOX_H / 2 - 4}
                y2={PLOT_Y + BOX_H / 2 + 4}
                stroke="transparent"
                strokeWidth={12}
                style={{ cursor: "pointer" }}
                onMouseEnter={showTip(`Median · ENSSA standard score ${stats.p50}`, scoreToPct(stats.p50))}
                onMouseLeave={hideTip}
              />
              <line
                x1={scoreToX(stats.p50, VB_W)}
                x2={scoreToX(stats.p50, VB_W)}
                y1={PLOT_Y - BOX_H / 2 - 4}
                y2={PLOT_Y + BOX_H / 2 + 4}
                stroke="var(--enssa-aubergine)"
                strokeWidth={3}
                strokeLinecap="round"
                style={{ pointerEvents: "none" }}
              />

            </g>
          )}

          {/* Axis baseline */}
          <line x1={0} x2={VB_W} y1={AXIS_Y} y2={AXIS_Y} stroke="var(--ink-300)" strokeWidth={1} />
        </svg>

        {outliers.map((outlier, index) => {
          const size = isNarrow ? 8 : 10;
          return (
            <span
              key={`outlier-${index}-${outlier.value}`}
              aria-hidden="true"
              style={{
                position: "absolute",
                left: `${scoreToPct(outlier.value)}%`,
                top: outlier.y - size / 2,
                transform: "translateX(-50%)",
                width: size,
                height: size,
                borderRadius: "50%",
                background: "var(--enssa-aubergine)",
                opacity: 0.82,
                pointerEvents: "none",
              }}
            />
          );
        })}
      </div>

      {/* Tick row — sits ABOVE the band labels. Reads top-down:
          numbers anchor the axis, labels name the regions, caption
          summarises. */}
      {!isNarrow && (
        <div
          style={{
            position: "relative",
            height: 14,
            marginTop: 2,
            fontSize: 11,
            color: "var(--fg-3)",
            fontFamily: "var(--font-mono)",
          }}
        >
          {ticks.map((tick) => (
            <span
              key={tick.label}
              style={{
                position: "absolute",
                left: `${((tick.value - AXIS_MIN) / AXIS_RANGE) * 100}%`,
                transform: "translateX(-50%)",
              }}
            >
              {tick.label}
            </span>
          ))}
        </div>
      )}

      {/* Band scale — visual rhyme with the Position chart's band
          scale. No "active" highlight here because the box-plot is
          a group view with no individual marker; all three labels
          render at full opacity. */}
      <div
        style={{
          position: "relative",
          height: isNarrow ? 13 : 18,
          marginTop: 6,
          fontSize: isNarrow ? 9 : 11,
          fontWeight: 700,
          letterSpacing: isNarrow ? "0.02em" : "0.04em",
          textTransform: "uppercase",
        }}
      >
        {BANDS.map((band) => {
          const leftPct = ((band.from - AXIS_MIN) / AXIS_RANGE) * 100;
          const widthPct = ((band.to - band.from) / AXIS_RANGE) * 100;
          const label = isNarrow ? band.shortLabel : band.label;
          return (
            <span
              key={`band-label-${band.key}`}
              style={{
                position: "absolute",
                left: `${leftPct}%`,
                width: `${widthPct}%`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: band.fg,
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {!isNarrow && (
                <span
                  aria-hidden="true"
                  style={{
                    height: 3,
                    flex: 1,
                    background: band.stroke,
                    opacity: 0.32,
                    borderRadius: 999,
                    marginRight: 8,
                  }}
                />
              )}
              {label}
              {!isNarrow && (
                <span
                  aria-hidden="true"
                  style={{
                    height: 3,
                    flex: 1,
                    background: band.stroke,
                    opacity: 0.32,
                    borderRadius: 999,
                    marginLeft: 8,
                  }}
                />
              )}
            </span>
          );
        })}
      </div>

      {/* Caption — kept as a static reading even with hover tooltips
          available, so the chart still tells its story when no one is
          hovering. */}
      <div style={{ marginTop: 10, fontSize: 11, color: "var(--fg-3)", textAlign: "center" }}>
        {hasData ? "" : "No overall ENSSA standard score available for this group."}
        {stats.unavailable > 0 ? ` ${stats.unavailable} students have no overall ENSSA standard score for this term.` : ""}
      </div>
    </div>
  );
}
